import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useUIStore } from '../../stores/uiStore';
import { useSSEStore } from '../../stores/sseStore';
import {
  Search, X, Package,
  ShoppingCart, Truck, AlertTriangle, MapPin, Receipt, ChevronDown,
  ExternalLink, Eye, Navigation, Check, Warehouse, PackageCheck, Send
} from 'lucide-react';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { EntityLink } from '../../components/shared/EntityLink';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../../components/shared/Pagination';
import { useCan, visibleTabs, pickActiveTab, Can, NoAccess, TAB_CAP } from '../../lib/rbac';

type Tab = 'orders' | 'claims';

// Orden de las pestañas (para caer a la primera permitida); capacidades en TAB_CAP.sales.
const TAB_ORDER: { key: Tab }[] = [{ key: 'orders' }, { key: 'claims' }];

const ORDER_STATUS_MAP: Record<string, 'ok' | 'warning' | 'error' | 'info' | 'unknown'> = {
  paid: 'ok', cancelled: 'error', partially_refunded: 'warning',
};

const SHIP_STATUS_MAP: Record<string, 'ok' | 'warning' | 'error' | 'info' | 'unknown'> = {
  delivered: 'ok', shipped: 'info', ready_to_ship: 'warning', not_delivered: 'error', cancelled: 'error',
};

const CLAIM_STATUS_MAP: Record<string, 'ok' | 'warning' | 'error' | 'info' | 'unknown'> = {
  opened: 'error', closed: 'unknown',
};

export default function SalesPage() {
  const { t } = useTranslation();
  const can = useCan();
  const [selectedTab, setSelectedTab] = useState<Tab>('orders');
  // Pestaña efectiva: la elegida si está permitida; si no, la primera permitida.
  const allowedTabs = visibleTabs('sales', TAB_ORDER, can);
  const firstAllowed = pickActiveTab('sales', TAB_ORDER, selectedTab, can) as Tab | null;
  const tab: Tab = firstAllowed ?? selectedTab;
  const setTab = setSelectedTab;
  useEffect(() => {
    if (firstAllowed && firstAllowed !== selectedTab) setSelectedTab(firstAllowed);
  }, [firstAllowed, selectedTab]);
  const [stats, setStats] = useState<any>(null);
  const [data, setData] = useState<any[]>([]);
  const { sellerId } = useUIStore();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [shipFilter, setShipFilter] = useState('all');
  const pag = usePagination('sales');
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [detailData, setDetailData] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [billingData, setBillingData] = useState<any>(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingOpen, setBillingOpen] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({
      seller_id: String(sellerId),
      page: String(pag.page),
      per_page: String(pag.perPage),
    });
    if (search) params.set('search', search);

    if (tab === 'orders') {
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (shipFilter !== 'all') params.set('shipment_status', shipFilter);

      const [dataRes, statsRes] = await Promise.all([
        api.get<any[]>(`/sales/orders?${params}`),
        api.get<any>(`/sales/stats?seller_id=${sellerId}`),
      ]);
      if (dataRes.success && dataRes.data) {
        setData(dataRes.data);
        pag.setTotal((dataRes as any).meta?.total || 0);
      }
      if (statsRes.success && statsRes.data) setStats(statsRes.data);
    } else {
      const dataRes = await api.get<any[]>(`/sales/claims?${params}`);
      if (dataRes.success && dataRes.data) {
        setData(dataRes.data);
        pag.setTotal((dataRes as any).meta?.total || 0);
      }
    }
    setLoading(false);
  }, [tab, sellerId, pag.page, pag.perPage, statusFilter, shipFilter, search]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { pag.resetPage(); }, [sellerId]);

  // SSE real-time
  const { subscribe } = useSSEStore();
  const loadRef = useRef(loadData);
  loadRef.current = loadData;
  useEffect(() => {
    const refresh = () => loadRef.current();
    const unsubs = [
      subscribe('question:answered', refresh),
      subscribe('claim:message', refresh),
      subscribe('claim:refunded', refresh),
      subscribe('claim:return', refresh),
      subscribe('claim:dispute', refresh),
    ];
    return () => unsubs.forEach(u => u());
  }, [subscribe]);

  const loadDetail = async (orderId: number) => {
    setDetailLoading(true);
    const res = await api.get<any>(`/sales/orders/${orderId}?seller_id=${sellerId}`);
    if (res.success && res.data) setDetailData(res.data);
    setDetailLoading(false);
  };

  const loadBilling = async (mlOrderId: string) => {
    setBillingLoading(true);
    setBillingData(null);
    const res = await api.get<any>(`/finance/billing/${mlOrderId}?seller_id=${sellerId}`);
    if (res.success && res.data) setBillingData(res.data);
    setBillingLoading(false);
    setBillingOpen(true);
  };

  const handleSearch = (val: string) => {
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => { setSearch(val); pag.resetPage(); }, 400);
    setSearchTimeout(timeout);
  };

  // Sin la llave del dinero, todo importe se muestra como "—".
  const canMoney = can('global.view_money');
  const fmt = (n: number) => {
    if (!canMoney) return '—';
    return n != null ? `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-';
  };
  const fmtDate = (d: string) => d ? new Date(d).toLocaleDateString() : '-';
  const fmtDateTime = (d: string) => d ? new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

  // Build timeline steps from order detail data (includes warehouse)
  const buildTimelineSteps = (detail: any) => {
    if (!detail) return [];
    const shipment = detail.shipment;
    const purchase = detail.purchase;
    const wh = detail.warehouse;
    const shipStatus = shipment?.status;

    type StepStatus = 'completed' | 'current' | 'future';
    type StepIcon = 'check' | 'warehouse' | 'pack' | 'send' | 'truck' | 'delivered' | 'cart' | 'dollar';

    const steps: { key: string; label: string; date: string; status: StepStatus; icon?: StepIcon; detail?: string }[] = [];

    const isPaid = detail.status === 'paid' || detail.status === 'partially_refunded';
    const isCancelled = detail.status === 'cancelled';
    const hasPurchase = !!purchase;
    const purchaseDelivered = purchase?.amazon_status === 'delivered' || purchase?.amazon_status === 'delivered_ml';
    const isReceived = !!wh?.received_at;
    const isPacked = !!wh?.packed_at;
    const isClosed = !!wh?.closed_at;
    const batchReleased = !!wh?.batch_released_at;
    const isDispatched = isClosed || batchReleased;
    const isShipped = shipStatus === 'shipped' || shipStatus === 'delivered';
    const isDelivered = shipStatus === 'delivered';

    // Helper: determine step status based on whether this and previous step are done
    const stepStatus = (done: boolean, prevDone: boolean): StepStatus =>
      done ? 'completed' : (prevDone && !done ? 'current' : 'future');

    // 1. Creada
    steps.push({
      key: 'created', label: t('sales.timelineCreated'),
      date: fmtDateTime(detail.date_created), status: 'completed', icon: 'cart',
    });

    // 2. Pagada
    steps.push({
      key: 'paid', label: t('sales.timelinePaid'),
      date: isPaid ? fmtDateTime(detail.date_created) : '',
      status: isCancelled ? 'future' : (isPaid ? 'completed' : 'current'),
      icon: 'dollar',
    });

    // 3. Comprada (Amazon)
    steps.push({
      key: 'purchased', label: t('sales.timelinePurchased'),
      date: hasPurchase ? fmtDateTime(purchase.created_at) : '',
      status: stepStatus(hasPurchase, isPaid),
      icon: 'cart',
      detail: hasPurchase ? purchase.amazon_status : undefined,
    });

    // 4. Recibida en WH
    steps.push({
      key: 'received', label: t('sales.timelineReceived'),
      date: isReceived ? fmtDateTime(wh.received_at) : '',
      status: stepStatus(isReceived, hasPurchase && purchaseDelivered),
      icon: 'warehouse',
      detail: isReceived && wh.batch_number ? `Lote ${wh.batch_number}` : undefined,
    });

    // 5. Empacada
    steps.push({
      key: 'packed', label: t('sales.timelinePacked'),
      date: isPacked ? fmtDateTime(wh.packed_at) : '',
      status: stepStatus(isPacked, isReceived),
      icon: 'pack',
    });

    // 6. Despachada (batch released / item closed)
    steps.push({
      key: 'dispatched', label: t('sales.timelineDispatched'),
      date: isDispatched ? fmtDateTime(wh.closed_at || wh.batch_released_at) : '',
      status: stepStatus(isDispatched, isPacked || isReceived),
      icon: 'send',
    });

    // 7. Enviada (ML shipment shipped)
    steps.push({
      key: 'shipped', label: t('sales.timelineShipped'),
      date: isShipped ? fmtDateTime(shipment?.shipped_at || shipment?.date_shipped) : '',
      status: stepStatus(isShipped, isDispatched),
      icon: 'truck',
    });

    // 8. Entregada
    steps.push({
      key: 'delivered', label: t('sales.timelineDelivered'),
      date: isDelivered ? fmtDateTime(shipment?.delivered_at || shipment?.last_updated) : '',
      status: stepStatus(isDelivered, isShipped),
      icon: 'delivered',
    });

    return steps;
  };

  // Icon for timeline step
  const TimelineIcon = ({ icon, status }: { icon?: string; status: string }) => {
    const cls = 'w-3 h-3';
    if (status === 'completed') return <Check className={`${cls} text-white`} />;
    if (status === 'current') return <div className="w-2 h-2 rounded-full bg-white" />;
    // Future: show contextual icon in muted color
    switch (icon) {
      case 'warehouse': return <Warehouse className={`${cls} text-gray-400`} />;
      case 'pack': return <PackageCheck className={`${cls} text-gray-400`} />;
      case 'send': return <Send className={`${cls} text-gray-400`} />;
      case 'truck': return <Truck className={`${cls} text-gray-400`} />;
      default: return null;
    }
  };

  // Determine ML site domain for order link
  const getMlOrderUrl = (detail: any) => {
    const siteMap: Record<string, string> = {
      MLA: 'mercadolibre.com.ar',
      MLM: 'mercadolibre.com.mx',
      MLC: 'mercadolibre.cl',
      MCO: 'mercadolibre.com.co',
      MLU: 'mercadolibre.com.uy',
      MLB: 'mercadolibre.com.br',
    };
    const domain = siteMap[detail.context_site] || 'mercadolibre.com.ar';
    return `https://www.${domain}/ventas/${detail.ml_order_id}/detalle`;
  };

  if (allowedTabs.length === 0) return <NoAccess />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl sm:text-2xl font-bold text-[var(--color-text)]">{t('sales.title')}</h1>
      </div>

      {/* Stats */}
      {stats && tab === 'orders' && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
          {[
            { label: t('sales.paid'), value: stats.paid, color: 'var(--color-success)' },
            { label: t('sales.readyToShip'), value: stats.ready_to_ship, color: 'var(--color-warning)' },
            { label: t('sales.shipped'), value: stats.shipped, color: 'var(--color-primary)' },
            { label: t('sales.delivered'), value: stats.delivered, color: 'var(--color-success)' },
            { label: t('sales.notDelivered'), value: stats.not_delivered, color: 'var(--color-error)' },
            { label: t('sales.cancelled'), value: stats.cancelled, color: 'var(--color-error)' },
            ...(canMoney ? [{ label: t('sales.totalRevenue'), value: fmt(stats.total_revenue), color: 'var(--color-text)' }] : []),
            { label: t('sales.openClaims'), value: stats.open_claims, color: stats.open_claims > 0 ? 'var(--color-error)' : 'var(--color-text)' },
          ].map((s, i) => (
            <div key={i} className="bg-[var(--color-bg-card)] rounded-lg border border-[var(--color-border)] p-3 text-center">
              <p className="text-xs text-[var(--color-text-secondary)] truncate">{s.label}</p>
              <p className="text-lg font-bold mt-1" style={{ color: s.color }}>{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[var(--color-border)]">
        <Can cap={TAB_CAP.sales.orders}>
          <button onClick={() => { setTab('orders'); pag.resetPage(); }}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'orders' ? 'border-[var(--color-primary)] text-[var(--color-primary)]' : 'border-transparent text-[var(--color-text-secondary)]'}`}>
            {t('sales.orders')}
          </button>
        </Can>
        <Can cap={TAB_CAP.sales.claims}>
          <button onClick={() => { setTab('claims'); pag.resetPage(); }}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'claims' ? 'border-[var(--color-primary)] text-[var(--color-primary)]' : 'border-transparent text-[var(--color-text-secondary)]'}`}>
            {t('sales.claims')} {stats?.open_claims > 0 && <span className="ml-1 px-1.5 py-0.5 bg-red-100 text-red-700 rounded-full text-xs">{stats.open_claims}</span>}
          </button>
        </Can>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-0 sm:min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-text-secondary)]" />
          <input type="text" placeholder={t('sales.searchPlaceholder')} defaultValue={search}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]" />
        </div>
        {tab === 'orders' && (
          <>
            <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); pag.resetPage(); }}
              className="px-3 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm">
              <option value="all">{t('common.status')}: {t('catalog.allProducts')}</option>
              <option value="paid">{t('sales.paid')}</option>
              <option value="cancelled">{t('sales.cancelled')}</option>
            </select>
            <select value={shipFilter} onChange={e => { setShipFilter(e.target.value); pag.resetPage(); }}
              className="px-3 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm">
              <option value="all">{t('sales.shipmentStatus')}: {t('catalog.allProducts')}</option>
              <option value="ready_to_ship">{t('sales.readyToShip')}</option>
              <option value="shipped">{t('sales.shipped')}</option>
              <option value="delivered">{t('sales.delivered')}</option>
              <option value="not_delivered">{t('sales.notDelivered')}</option>
            </select>
          </>
        )}
        <span className="text-sm text-[var(--color-text-secondary)]">{pag.total.toLocaleString()} {tab === 'orders' ? t('sales.orders') : t('sales.claims')}</span>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-3 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : tab === 'orders' ? (
        data.length === 0 ? (
          <div className="text-center py-12 text-[var(--color-text-secondary)]">
            <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>{t('sales.noOrders')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto border border-[var(--color-border)] rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-[var(--color-bg-secondary)] border-b border-[var(--color-border)]">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-[var(--color-text-secondary)]">{t('catalog.product')}</th>
                  <th className="text-left px-3 py-2 font-medium text-[var(--color-text-secondary)]">{t('sales.buyer')}</th>
                  <th className="text-right px-3 py-2 font-medium text-[var(--color-text-secondary)]">{t('common.price')}</th>
                  <th className="text-right px-3 py-2 font-medium text-[var(--color-text-secondary)]">{t('sales.profit')}</th>
                  <th className="text-center px-3 py-2 font-medium text-[var(--color-text-secondary)]">{t('common.status')}</th>
                  <th className="text-center px-3 py-2 font-medium text-[var(--color-text-secondary)]">{t('sales.shipmentStatus')}</th>
                  <th className="text-left px-3 py-2 font-medium text-[var(--color-text-secondary)]">{t('sales.site')}</th>
                  <th className="text-left px-3 py-2 font-medium text-[var(--color-text-secondary)]">{t('common.date')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--color-border)]">
                {data.map((o: any) => (
                  <tr key={o.id} className="hover:bg-[var(--color-bg-secondary)] cursor-pointer transition-colors"
                    onClick={() => { setSelectedItem(o); loadDetail(o.id); }}>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        {o.product_image ? (
                          <img src={o.product_image} alt="" className="w-10 h-10 rounded object-cover bg-white flex-shrink-0" />
                        ) : (
                          <div className="w-10 h-10 rounded bg-[var(--color-bg-secondary)] flex items-center justify-center flex-shrink-0">
                            <Package className="w-5 h-5 text-[var(--color-text-secondary)]" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="font-medium text-[var(--color-text)] text-xs">{o.product_title || o.asin || '—'}</p>
                          <div className="text-xs"><EntityLink type="ml_order" value={o.ml_order_id} sellerId={sellerId} /></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs text-[var(--color-text-secondary)]">{o.buyer_nickname || '—'}</td>
                    <td className="px-3 py-2 text-right text-sm font-mono">{fmt(o.paid_amount)}</td>
                    <td className={`px-3 py-2 text-right text-sm font-mono ${(o.gross_profit ?? 0) >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                      {o.gross_profit != null ? fmt(o.gross_profit) : '—'}
                      {o.profit_margin != null && canMoney && <span className="text-xs ml-1">({Number(o.profit_margin).toFixed(1)}%)</span>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <StatusBadge status={ORDER_STATUS_MAP[o.status] || 'unknown'} label={o.status} size="sm" />
                    </td>
                    <td className="px-3 py-2 text-center">
                      {o.shipment_status ? (
                        <StatusBadge status={SHIP_STATUS_MAP[o.shipment_status] || 'unknown'} label={o.shipment_status} size="sm" />
                      ) : '—'}
                    </td>
                    <td className="px-3 py-2 text-xs text-[var(--color-text-secondary)]">{o.context_site || '—'}</td>
                    <td className="px-3 py-2 text-xs text-[var(--color-text-secondary)]">{fmtDate(o.date_created)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        data.length === 0 ? (
          <div className="text-center py-12 text-[var(--color-text-secondary)]">
            <AlertTriangle className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>{t('sales.noClaims')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto border border-[var(--color-border)] rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-[var(--color-bg-secondary)] border-b border-[var(--color-border)]">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-[var(--color-text-secondary)]">Claim</th>
                  <th className="text-left px-3 py-2 font-medium text-[var(--color-text-secondary)]">{t('sales.claimType')}</th>
                  <th className="text-left px-3 py-2 font-medium text-[var(--color-text-secondary)]">{t('sales.claimReason')}</th>
                  <th className="text-center px-3 py-2 font-medium text-[var(--color-text-secondary)]">{t('sales.claimStatus')}</th>
                  <th className="text-left px-3 py-2 font-medium text-[var(--color-text-secondary)]">{t('sales.resolution')}</th>
                  <th className="text-left px-3 py-2 font-medium text-[var(--color-text-secondary)]">{t('sales.buyer')}</th>
                  <th className="text-right px-3 py-2 font-medium text-[var(--color-text-secondary)]">{t('common.price')}</th>
                  <th className="text-left px-3 py-2 font-medium text-[var(--color-text-secondary)]">{t('common.date')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--color-border)]">
                {data.map((c: any) => (
                  <tr key={c.id} className="hover:bg-[var(--color-bg-secondary)] transition-colors">
                    <td className="px-3 py-2 text-xs font-mono">#{c.ml_claim_id}</td>
                    <td className="px-3 py-2 text-xs">{c.type || '—'}</td>
                    <td className="px-3 py-2 text-xs text-[var(--color-text-secondary)]">{c.reason_id || '—'}</td>
                    <td className="px-3 py-2 text-center">
                      <StatusBadge status={CLAIM_STATUS_MAP[c.status] || 'unknown'} label={c.status} size="sm" />
                    </td>
                    <td className="px-3 py-2 text-xs">{
                      Array.isArray(c.resolution_benefited) && c.resolution_benefited.length > 0
                        ? c.resolution_benefited.join(', ')
                        : '—'
                    }</td>
                    <td className="px-3 py-2 text-xs text-[var(--color-text-secondary)]">{c.buyer_nickname || '—'}</td>
                    <td className="px-3 py-2 text-right text-sm font-mono">{c.paid_amount ? fmt(c.paid_amount) : '—'}</td>
                    <td className="px-3 py-2 text-xs text-[var(--color-text-secondary)]">{fmtDate(c.date_created)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* Pagination */}
      <Pagination
        page={pag.page}
        totalPages={pag.totalPages}
        total={pag.total}
        perPage={pag.perPage}
        onPageChange={pag.setPage}
        onPerPageChange={pag.setPerPage}
      />

      {/* Order Detail Panel */}
      {selectedItem && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/30" onClick={() => { setSelectedItem(null); setDetailData(null); setBillingData(null); setBillingOpen(false); }} />
          <div className="relative w-full max-w-2xl bg-[var(--color-bg)] shadow-xl overflow-y-auto animate-slide-in">
            <div className="sticky top-0 bg-[var(--color-bg)] border-b border-[var(--color-border)] px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-lg font-bold text-[var(--color-text)]">{t('sales.orderDetail')}</h2>
              <button onClick={() => { setSelectedItem(null); setDetailData(null); setBillingData(null); setBillingOpen(false); }} className="p-1 hover:bg-[var(--color-bg-secondary)] rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            {detailLoading || !detailData ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-3 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="p-6 space-y-6">
                {/* Order header */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-lg font-bold"><EntityLink type="ml_order" value={detailData.ml_order_id} sellerId={sellerId} /></p>
                    <p className="text-sm text-[var(--color-text-secondary)]">{detailData.buyer_nickname} · {detailData.context_site}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold">{fmt(detailData.paid_amount)}</p>
                    <StatusBadge status={ORDER_STATUS_MAP[detailData.status] || 'unknown'} label={detailData.status} />
                  </div>
                </div>

                {/* Order Timeline (with Warehouse steps) */}
                {(() => {
                  const steps = buildTimelineSteps(detailData);
                  return (
                    <div className="bg-[var(--color-bg-secondary)] rounded-lg p-4">
                      <h4 className="text-sm font-medium text-[var(--color-text)] mb-4 flex items-center gap-2">
                        {t('sales.timeline')}
                        {detailData.warehouse?.batch_number && (
                          <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 rounded-full">
                            Lote {detailData.warehouse.batch_number}
                          </span>
                        )}
                        {detailData.warehouse?.wh_status && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            detailData.warehouse.wh_status === 'closed' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : detailData.warehouse.wh_status === 'packed' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                            : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                          }`}>
                            WH: {detailData.warehouse.wh_status}
                          </span>
                        )}
                      </h4>
                      <div className="relative flex items-start justify-between">
                        {/* Connecting line */}
                        <div className="absolute top-3 left-0 right-0 h-0.5 bg-[var(--color-border)]" />
                        {/* Progress line */}
                        {(() => {
                          const lastCompleted = steps.reduce((acc: number, s: any, i: number) => s.status === 'completed' ? i : acc, 0);
                          const currentIdx = steps.findIndex((s: any) => s.status === 'current');
                          const progressTo = currentIdx >= 0 ? currentIdx : lastCompleted;
                          const pct = steps.length > 1 ? (progressTo / (steps.length - 1)) * 100 : 0;
                          return <div className="absolute top-3 left-0 h-0.5 bg-green-500 transition-all duration-500" style={{ width: `${pct}%` }} />;
                        })()}
                        {steps.map((step: any) => (
                          <div key={step.key} className="relative flex flex-col items-center z-10" style={{ width: `${100 / steps.length}%` }}>
                            {/* Dot */}
                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                              step.status === 'completed'
                                ? 'bg-green-500 border-green-500'
                                : step.status === 'current'
                                  ? 'bg-blue-500 border-blue-500 animate-pulse'
                                  : 'bg-[var(--color-bg)] border-[var(--color-border)]'
                            }`}>
                              <TimelineIcon icon={step.icon} status={step.status} />
                            </div>
                            {/* Label */}
                            <span className={`text-[10px] mt-1 font-medium text-center leading-tight ${
                              step.status === 'completed'
                                ? 'text-green-600 dark:text-green-400'
                                : step.status === 'current'
                                  ? 'text-blue-600 dark:text-blue-400'
                                  : 'text-[var(--color-text-secondary)]'
                            }`}>{step.label}</span>
                            {/* Date */}
                            {step.date && (
                              <span className="text-[9px] text-[var(--color-text-secondary)] mt-0.5 text-center leading-tight">{step.date}</span>
                            )}
                            {/* Detail (batch number, amazon status, etc.) */}
                            {step.detail && (
                              <span className="text-[9px] text-purple-600 dark:text-purple-400 mt-0.5 text-center leading-tight">{step.detail}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Quick Actions */}
                <div className="flex flex-wrap gap-2">
                  <a href={getMlOrderUrl(detailData)} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-yellow-50 text-yellow-700 border border-yellow-200 hover:bg-yellow-100 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800 dark:hover:bg-yellow-900/40 transition-colors">
                    <ExternalLink className="w-3.5 h-3.5" />
                    {t('sales.viewInML')}
                  </a>
                  {detailData.purchase && (
                    <button onClick={() => { window.location.hash = '#/purchases'; }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800 dark:hover:bg-blue-900/40 transition-colors">
                      <Eye className="w-3.5 h-3.5" />
                      {t('sales.viewPurchase')}
                    </button>
                  )}
                  {detailData.shipment?.tracking_number && (
                    <a href={`https://parcelsapp.com/en/tracking/${detailData.shipment.tracking_number}`} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-cyan-50 text-cyan-700 border border-cyan-200 hover:bg-cyan-100 dark:bg-cyan-900/20 dark:text-cyan-400 dark:border-cyan-800 dark:hover:bg-cyan-900/40 transition-colors">
                      <Navigation className="w-3.5 h-3.5" />
                      {t('sales.viewTracking')}
                    </a>
                  )}
                </div>

                {/* Items */}
                <div>
                  <h4 className="text-sm font-medium text-[var(--color-text)] mb-3">{t('sales.itemDetail')}</h4>
                  {detailData.items?.map((item: any, idx: number) => (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-[var(--color-bg-secondary)] rounded-lg mb-2">
                      {item.product_image ? (
                        <img src={item.product_image} alt="" className="w-16 h-16 rounded object-cover bg-white flex-shrink-0" />
                      ) : (
                        <div className="w-16 h-16 rounded bg-[var(--color-border)] flex items-center justify-center flex-shrink-0">
                          <Package className="w-8 h-8 text-[var(--color-text-secondary)]" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{item.title}</p>
                        <p className="text-xs text-[var(--color-text-secondary)] mt-1">
                          SKU: {item.seller_sku} · Qty: {item.quantity}
                        </p>
                        <div className="flex gap-4 mt-1 text-xs">
                          <span>{t('common.price')}: {fmt(item.unit_price)}</span>
                          <span className="text-[var(--color-text-secondary)]">Fee: {fmt(item.sale_fee)}</span>
                          <span className="font-medium">Net: {fmt(item.net_amount)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Shipping */}
                {detailData.shipment && (
                  <div className="bg-[var(--color-bg-secondary)] rounded-lg p-4">
                    <h4 className="text-sm font-medium text-[var(--color-text)] mb-3 flex items-center gap-2">
                      <Truck className="w-4 h-4" /> {t('sales.shippingInfo')}
                    </h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-[var(--color-text-secondary)]">{t('common.status')}:</span>
                        <span className="ml-2"><StatusBadge status={SHIP_STATUS_MAP[detailData.shipment.status] || 'unknown'} label={detailData.shipment.status} size="sm" /></span>
                      </div>
                      <div>
                        <span className="text-[var(--color-text-secondary)]">{t('purchases.tracking')}:</span>
                        <span className="ml-2 text-xs">{detailData.shipment.tracking_number ? <EntityLink type="tracking" value={detailData.shipment.tracking_number} sellerId={sellerId} /> : '—'}</span>
                      </div>
                      <Can cap="global.view_buyers">
                        <div className="col-span-2">
                          <span className="text-[var(--color-text-secondary)] flex items-center gap-1"><MapPin className="w-3 h-3" /> {t('sales.destination')}:</span>
                          <span className="ml-2 text-xs">
                            {[detailData.shipment.dest_city_name, detailData.shipment.dest_state_name, detailData.shipment.dest_country_name].filter(Boolean).join(', ')}
                          </span>
                        </div>
                      </Can>
                      {detailData.shipment.estimated_delivery_date && (
                        <div>
                          <span className="text-[var(--color-text-secondary)]">{t('sales.estimatedDelivery')}:</span>
                          <span className="ml-2">{fmtDate(detailData.shipment.estimated_delivery_date)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Purchase / Reconciliation — costos y margen: solo con la llave del dinero */}
                {detailData.purchase && canMoney && (
                  <div className="bg-[var(--color-bg-secondary)] rounded-lg p-4">
                    <h4 className="text-sm font-medium text-[var(--color-text)] mb-3">{t('sales.purchaseInfo')}</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-[var(--color-text-secondary)]">{t('purchases.cost')}:</span>
                        <span className="ml-2 font-medium">{fmt(detailData.purchase.total_cost)}</span>
                      </div>
                      <div>
                        <span className="text-[var(--color-text-secondary)]">{t('sales.profit')}:</span>
                        <span className={`ml-2 font-bold ${(detailData.purchase.gross_profit ?? 0) >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                          {fmt(detailData.purchase.gross_profit)}
                        </span>
                      </div>
                      <div>
                        <span className="text-[var(--color-text-secondary)]">{t('sales.margin')}:</span>
                        <span className={`ml-2 font-medium ${(detailData.purchase.profit_margin ?? 0) >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                          {Number(detailData.purchase.profit_margin || 0).toFixed(1)}%
                        </span>
                      </div>
                      <div>
                        <span className="text-[var(--color-text-secondary)]">Amazon:</span>
                        <span className="ml-2"><StatusBadge status={ORDER_STATUS_MAP[detailData.purchase.amazon_status] || 'info'} label={detailData.purchase.amazon_status} size="sm" /></span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Claims */}
                {detailData.claims?.length > 0 && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-red-700 dark:text-red-400 mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" /> {t('sales.claims')} ({detailData.claims.length})
                    </h4>
                    {detailData.claims.map((c: any) => (
                      <div key={c.id} className="flex items-center justify-between py-2 text-sm">
                        <div>
                          <span className="font-mono text-xs">#{c.ml_claim_id}</span>
                          <span className="ml-2 text-[var(--color-text-secondary)]">{c.type} · {c.reason_id}</span>
                        </div>
                        <StatusBadge status={CLAIM_STATUS_MAP[c.status] || 'unknown'} label={c.status} size="sm" />
                      </div>
                    ))}
                  </div>
                )}

                {/* Billing ML — desglose de plata de Mercado Libre */}
                {canMoney && <div className="bg-[var(--color-bg-secondary)] rounded-lg overflow-hidden">
                  <button
                    onClick={() => {
                      if (!billingData && !billingLoading) {
                        loadBilling(detailData.ml_order_id);
                      } else {
                        setBillingOpen(o => !o);
                      }
                    }}
                    className="w-full flex items-center justify-between p-4 hover:bg-[var(--color-border)] transition-colors"
                  >
                    <h4 className="text-sm font-medium text-[var(--color-text)] flex items-center gap-2">
                      <Receipt className="w-4 h-4" /> Billing ML
                    </h4>
                    <div className="flex items-center gap-2">
                      {!billingData && !billingLoading && (
                        <span className="text-xs text-[var(--color-primary)]">Ver Billing ML</span>
                      )}
                      {billingLoading && (
                        <div className="w-4 h-4 border-2 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin" />
                      )}
                      <ChevronDown className={`w-4 h-4 transition-transform ${billingOpen ? 'rotate-180' : ''}`} />
                    </div>
                  </button>

                  {billingOpen && billingData && (
                    <div className="px-4 pb-4 space-y-3">
                      {billingData.results?.length > 0 ? (
                        billingData.results.map((order: any, oi: number) => (
                          <div key={oi} className="space-y-3">
                            {/* Payment Info */}
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div>
                                <span className="text-[var(--color-text-secondary)]">Release Status:</span>
                                <span className={`ml-2 font-medium ${order.payment_info?.money_release_status === 'released' ? 'text-green-600' : 'text-yellow-600'}`}>
                                  {order.payment_info?.money_release_status || '—'}
                                </span>
                              </div>
                              <div>
                                <span className="text-[var(--color-text-secondary)]">Release Date:</span>
                                <span className="ml-2">{order.payment_info?.money_release_date ? fmtDate(order.payment_info.money_release_date) : '—'}</span>
                              </div>
                              <div>
                                <span className="text-[var(--color-text-secondary)]">Base Amount:</span>
                                <span className="ml-2 font-mono">{fmt(order.payment_info?.base_amount)}</span>
                              </div>
                              <div>
                                <span className="text-[var(--color-text-secondary)]">Base USD:</span>
                                <span className="ml-2 font-mono font-medium">{fmt(order.payment_info?.base_amount_usd)}</span>
                              </div>
                            </div>

                            {/* Tax Details */}
                            {order.payment_info?.tax_details?.length > 0 && (
                              <div>
                                <p className="text-xs font-medium text-[var(--color-text-secondary)] mb-1">Impuestos</p>
                                {order.payment_info.tax_details.map((tax: any, ti: number) => (
                                  <div key={ti} className="flex justify-between text-xs py-0.5">
                                    <span className="text-[var(--color-text-secondary)]">{tax.type || tax.name || 'Tax'}</span>
                                    <span className="font-mono">{fmt(tax.amount)}</span>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Fee Breakdown */}
                            {order.details?.length > 0 && (
                              <div>
                                <p className="text-xs font-medium text-[var(--color-text-secondary)] mb-1">Desglose de Cargos</p>
                                <div className="divide-y divide-[var(--color-border)]">
                                  {order.details.map((d: any, di: number) => (
                                    <div key={di} className="flex justify-between py-1.5 text-xs">
                                      <div>
                                        <span className="text-[var(--color-text)]">{d.charge_info?.transaction_detail || d.charge_info?.detail_type || 'Cargo'}</span>
                                        {d.charge_info?.detail_sub_type && (
                                          <span className="ml-1 text-[var(--color-text-secondary)]">({d.charge_info.detail_sub_type})</span>
                                        )}
                                      </div>
                                      <span className={`font-mono ${(d.charge_info?.detail_amount ?? 0) < 0 ? 'text-red-500' : ''}`}>
                                        {fmt(d.charge_info?.detail_amount)}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Net summary */}
                            <div className="flex justify-between items-center pt-2 border-t border-[var(--color-border)]">
                              <span className="text-sm font-medium">Neto USD</span>
                              <span className="text-lg font-bold font-mono">{fmt(order.payment_info?.base_amount_usd)}</span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-[var(--color-text-secondary)]">No se encontraron datos de billing para esta orden.</p>
                      )}
                    </div>
                  )}
                </div>}

                {/* Dates */}
                <div className="text-xs text-[var(--color-text-secondary)] grid grid-cols-2 gap-2">
                  <div>{t('catalog.created')}: {fmtDate(detailData.date_created)}</div>
                  <div>{t('catalog.updated')}: {fmtDate(detailData.last_updated)}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
